import { useProfile } from "@serophin/nottldr-shared/hooks";
import { Check, Pencil, X } from "lucide-preact";
import { useState } from "preact/hooks";
import { useToast } from "../../contexts/ToastContext";

const ICON = "p-1 disabled:opacity-50";

const useNameEdit = (name: string) => {
  const toast = useToast();
  const { updateName } = useProfile();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const start = () => {
    setDraft(name === "there" ? "" : name);
    setEditing(true);
  };

  const save = async () => {
    if (!draft.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await updateName(draft.trim());
      setEditing(false);
      toast.success("Name updated successfully");
    } catch (error) {
      console.error("Failed to update name:", error);
      toast.error("Failed to update name. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return { editing, draft, saving, setDraft, start, save, cancel: () => setEditing(false) };
};

type NameEdit = ReturnType<typeof useNameEdit>;

const EditRow = ({ edit }: { edit: NameEdit }) => (
  <div class="flex items-center gap-2">
    <span class="text-xl font-bold text-gray-900 dark:text-white">Hi </span>
    <input
      type="text"
      value={edit.draft}
      onInput={(event) => edit.setDraft(event.currentTarget.value)}
      onKeyDown={(event) => (event.key === "Enter" ? edit.save() : event.key === "Escape" && edit.cancel())}
      class="text-xl font-bold text-gray-900 dark:text-white bg-transparent border-b-2 border-blue-500 focus:outline-none px-1 max-w-48"
      placeholder="Your name"
      maxLength={100}
      autoFocus
      disabled={edit.saving}
    />
    <span class="text-xl font-bold text-gray-900 dark:text-white">!</span>
    <button
      type="button"
      onClick={edit.save}
      disabled={edit.saving}
      class={`${ICON} text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400`}
      title="Save"
    >
      <Check size={20} />
    </button>
    <button
      type="button"
      onClick={edit.cancel}
      disabled={edit.saving}
      class={`${ICON} text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300`}
      title="Cancel"
    >
      <X size={20} />
    </button>
  </div>
);

export const UserInfo = ({ email, name }: { email: string; name: string }) => {
  const edit = useNameEdit(name);
  return (
    <div class="bg-white dark:bg-neutral-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-3">
      <div class="flex flex-col space-y-2">
        {edit.editing ? (
          <EditRow edit={edit} />
        ) : (
          <div class="flex items-center gap-2">
            <h2 class="text-xl font-bold text-gray-900 dark:text-white">Hi {name === "there" ? "there" : name.split(" ")[0]}!</h2>
            <button
              type="button"
              onClick={edit.start}
              class="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              title="Edit name"
            >
              <Pencil size={16} />
            </button>
          </div>
        )}
        <div class="flex items-center text-gray-600 dark:text-gray-400 mb-1">
          <span class="text-sm">{email}</span>
        </div>
      </div>
    </div>
  );
};
